import { create } from 'zustand';
import { usePropertyStore } from './propertyStore';
import { usePendingJoinsStore } from './pendingJoinsStore';
import { useModuleStore } from './moduleStore';
import { queryClient } from '../lib/queryClient';
import { useExperienceStore } from './experienceStore';
import { secureStorage } from '../lib/secureStorage';
import {
  BIOMETRIC_SESSION_GONE,
  clearBiometricSession,
  hasBiometricSession,
  readBiometricSession,
  saveBiometricSession,
  type BiometricSession,
} from '../lib/biometricSession';
import { clearCachedDownloads } from '../lib/downloadBlob';
import { revokeRefreshToken } from '../api/authRefresh';
import { useHomeLayoutStore } from './homeLayoutStore';
import { useQuickLinksStore } from './quickLinksStore';
import { useNewsReadStore } from './newsReadStore';
import { useSettingsStore } from './settingsStore';
import { clearCart } from '../../features/cart/api';
import { unregisterStoredToken } from '../../features/notifications/pushToken';
import type {
  User,
  AuthResponse,
  CorePortalAuthResponse,
  LinkedTenant,
} from '../types/auth';

/**
 * Auth store — ports AuthService state from the Angular member portal.
 *
 * Storage strategy:
 *   - Access token   : in memory only. A cold start renews it from the refresh token before the
 *                      app renders (`restoring`), so it never touches storage.
 *   - Refresh + SSO  : where the member chose (see `writeSecrets`): behind the device biometric
 *     session tokens   when biometric unlock is on, else secure storage (Keychain/Keystore on
 *                      native; localStorage on web) while "remember me" is on, else nowhere.
 *   - User profile   : localStorage snapshot for offline first paint, while "remember me" is on.
 *                      Never mirrored to native Preferences.
 *   - Tenant + URL   : localStorage (paas_tenant_id, paas_regional_api_url) — keys preserved
 *                      verbatim from Angular for cross-build interop.
 *   - SSO session    : secure storage (paas_sso_session_token).
 *   - Linked tenants : localStorage (paas_sso_tenants) — list, not credentials.
 *   - Active portal  : localStorage (paas_active_portal_domain) — last domain used after login
 *                      so dev refreshes don't re-resolve the SSO hub.
 *
 * Storage keys mirror Angular constants exactly.
 */

/** Where builds before the in-memory access token kept it. Deleted on hydrate. */
const LEGACY_ACCESS_TOKEN_KEY = 'paas_access_token';
const REFRESH_TOKEN_KEY = 'paas_refresh_token';
const USER_KEY = 'paas_user';
const SSO_SESSION_KEY = 'paas_sso_session_token';
const SSO_TENANTS_KEY = 'paas_sso_tenants';
const ACTIVE_PORTAL_DOMAIN_KEY = 'paas_active_portal_domain';
const ACTIVE_COMMUNITY_TENANT_KEY = 'paas_active_community_tenant_id';
const TENANT_ID_KEY = 'paas_tenant_id';
const REGIONAL_API_URL_KEY = 'paas_regional_api_url';
const REMEMBER_SESSION_KEY = 'paas_remember_session';

interface AuthPersistenceOptions {
  remember?: boolean;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
  tenantId: string | null;
  regionalApiUrl: string;
  ssoSessionToken: string | null;
  linkedTenants: LinkedTenant[];
  rememberSession: boolean;
  /** A stored session is being renewed. The app shows a loading state until it settles. */
  restoring: boolean;
  /** A session bound to the biometric waits for the member to unlock it. */
  locked: boolean;

  isAuthenticated: () => boolean;

  /** Persist tokens + user after a successful auth response. */
  setAuth: (response: AuthResponse | CorePortalAuthResponse, options?: AuthPersistenceOptions) => Promise<void>;

  /** Persist a refreshed token pair, leaving the cached user in place. */
  setTokens: (accessToken: string, refreshToken?: string | null) => Promise<void>;

  /** Merge freshly-resolved licence + permissions into the cached user. */
  applySessionAccess: (input: { license?: User['license'] | null; permissions?: string[] }) => void;

  setRegionalApiUrl: (url: string) => void;
  setTenantId: (tenantId: string | null) => void;
  setActivePortalDomain: (domain: string | null | undefined) => void;
  setSso: (response: CorePortalAuthResponse, options?: AuthPersistenceOptions) => Promise<void>;
  /** Persist a refreshed SSO session token (sliding refresh, keeps tenant switching alive). */
  setSsoSessionToken: (token: string) => Promise<void>;
  setLinkedTenants: (tenants: LinkedTenant[]) => void;
  setRememberSession: (remember: boolean) => void;

  /** Turn biometric unlock on or off: moves the session's tokens behind the biometric, or out. */
  setBiometricUnlock: (enabled: boolean) => Promise<void>;

  /**
   * Prompt the biometric and load the locked session's tokens. False when the member cancels or the
   * match fails; a session whose item is gone (biometrics changed) is signed out.
   */
  unlock: (reason: string) => Promise<boolean>;

  /** Clear everything — use on full sign-out. */
  signOut: () => Promise<void>;

  /** Drop a previous session's residue when no session is live. No-op while signed in. */
  clearStaleSession: () => void;

  /** Read-only helpers for axios interceptors and guards. */
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  getTenantId: () => string | null;
  getRegionalApiUrl: () => string;

  /** Hydrate from storage on cold start. Idempotent. */
  hydrate: () => Promise<void>;
}

function readJSON<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function syncLicenseModules(user: User | null): void {
  useModuleStore.getState().syncLicensedModules(user?.license?.enabled_modules ?? []);
}

/**
 * The tenant the current session belongs to — the one that issued the JWT.
 *
 * Distinct from `authStore.tenantId`, which is *ambient*: it names whichever tenant the app has
 * most recently resolved a domain for, and every `/resolve-domain` rewrites it. Only the session
 * tenant can be trusted to still name the community a signed-in member's data lives in.
 */
export function getSessionTenantId(): string | undefined {
  const user = useAuthStore.getState().user;
  if (user) return user.tenant_id;

  const token = useAuthStore.getState().getAccessToken();
  if (!token) return undefined;
  try {
    const payload = JSON.parse(atob(token.split('.')[1] ?? '')) as { tenant_id?: string };
    return payload.tenant_id;
  } catch {
    return undefined;
  }
}

/** Authenticated tenant user id (the session user's, else the JWT's `user_id`). */
export function getSessionUserId(): string | undefined {
  const user = useAuthStore.getState().user;
  if (user) return user.user_id;

  const token = useAuthStore.getState().getAccessToken();
  if (!token) return undefined;
  try {
    const payload = JSON.parse(atob(token.split('.')[1] ?? '')) as { user_id?: string };
    return payload.user_id;
  } catch {
    return undefined;
  }
}

function persistRememberSession(remember: boolean): void {
  localStorage.setItem(REMEMBER_SESSION_KEY, JSON.stringify(remember));
}

const writeSecret = (key: string, value: string | null): Promise<void> =>
  value ? secureStorage.set(key, value) : secureStorage.remove(key);

/**
 * Store the session's refresh and SSO session tokens where the member chose.
 *   1. Biometric unlock on: only the biometric-bound item holds them.
 *   2. Otherwise secure storage while "remember me" is on, and nowhere when it is off.
 */
async function writeSecrets({ refreshToken, ssoSessionToken, rememberSession }: AuthState): Promise<void> {
  if (refreshToken && rememberSession && useSettingsStore.getState().biometricEnabled) {
    await saveBiometricSession({ refreshToken, ssoSessionToken });
    await secureStorage.remove(REFRESH_TOKEN_KEY);
    await secureStorage.remove(SSO_SESSION_KEY);
    return;
  }

  await clearBiometricSession();
  await writeSecret(REFRESH_TOKEN_KEY, rememberSession ? refreshToken : null);
  await writeSecret(SSO_SESSION_KEY, rememberSession ? ssoSessionToken : null);
}

let persisting: Promise<void> = Promise.resolve();

/** One write at a time, each reading the state when it runs, so the last write holds the latest tokens. */
function persistSecrets(): Promise<void> {
  const write = () => writeSecrets(useAuthStore.getState());
  persisting = persisting.then(write, write);
  return persisting;
}

let signingOut: Promise<void> | null = null;

export const useAuthStore = create<AuthState>((set, get) => ({
  accessToken: null,
  refreshToken: null,
  user: null,
  tenantId: null,
  regionalApiUrl: '',
  ssoSessionToken: null,
  linkedTenants: [],
  rememberSession: true,
  restoring: false,
  locked: false,

  isAuthenticated() {
    return !!(get().accessToken || get().refreshToken);
  },

  async setAuth(response, options) {
    if (!response.success) return;
    const { accessToken, refreshToken, user } = response;
    const rememberSession = options?.remember ?? get().rememberSession;
    set({ accessToken, refreshToken, user, rememberSession });
    syncLicenseModules(user);
    persistRememberSession(rememberSession);

    // The profile snapshot paints the app on the next cold start while the session renews.
    if (rememberSession) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);

    await persistSecrets();
  },

  async setTokens(accessToken, refreshToken) {
    set({ accessToken, refreshToken: refreshToken ?? get().refreshToken });
    await persistSecrets();
  },

  applySessionAccess({ license, permissions }) {
    const user = get().user;
    if (!user) return;

    const next: User = {
      ...user,
      ...(license ? { license } : {}),
      ...(Array.isArray(permissions) ? { permissions } : {}),
    };
    set({ user: next });
    syncLicenseModules(next);

    if (get().rememberSession) localStorage.setItem(USER_KEY, JSON.stringify(next));
  },

  setRegionalApiUrl(url) {
    set({ regionalApiUrl: url });
    if (url) localStorage.setItem(REGIONAL_API_URL_KEY, url);
  },

  setTenantId(tenantId) {
    set({ tenantId });
    if (tenantId) localStorage.setItem(TENANT_ID_KEY, tenantId);
    else localStorage.removeItem(TENANT_ID_KEY);
  },

  setActivePortalDomain(domain) {
    const d = domain?.trim();
    if (d) localStorage.setItem(ACTIVE_PORTAL_DOMAIN_KEY, d);
    else localStorage.removeItem(ACTIVE_PORTAL_DOMAIN_KEY);
  },

  async setSso(response, options) {
    const rememberSession = options?.remember ?? get().rememberSession;
    set({ rememberSession });
    persistRememberSession(rememberSession);

    if (response.regional_api_base_url) {
      get().setRegionalApiUrl(response.regional_api_base_url);
    }
    if (response.tenant_id) {
      get().setTenantId(response.tenant_id);
    }
    if (response.domain) {
      get().setActivePortalDomain(response.domain);
    }
    if (response.tenant_id) {
      localStorage.setItem(ACTIVE_COMMUNITY_TENANT_KEY, response.tenant_id);
    }
    if (!response.tenant_id && response.domain) {
      localStorage.removeItem(ACTIVE_COMMUNITY_TENANT_KEY);
    }

    const sessionToken = response.sso?.session_token;
    if (sessionToken) set({ ssoSessionToken: sessionToken });

    if (Array.isArray(response.sso?.tenants)) {
      const tenants = response.sso.tenants;
      set({ linkedTenants: tenants });
      if (rememberSession) localStorage.setItem(SSO_TENANTS_KEY, JSON.stringify(tenants));
      else localStorage.removeItem(SSO_TENANTS_KEY);
    }

    await persistSecrets();
  },

  async setSsoSessionToken(token) {
    set({ ssoSessionToken: token });
    await persistSecrets();
  },

  setRememberSession(rememberSession) {
    set({ rememberSession });
    persistRememberSession(rememberSession);
  },

  async setBiometricUnlock(enabled) {
    // 1. Biometric unlock keeps the session, so it turns "remember me" on with it.
    const settings = useSettingsStore.getState();
    settings.setBiometricEnabled(enabled);
    if (enabled) get().setRememberSession(true);

    // 2. Move the tokens. A device that cannot hold the item (no passcode set) keeps the
    //    setting as it was, with the tokens still where they were.
    try {
      await persistSecrets();
    } catch (error) {
      settings.setBiometricEnabled(!enabled);
      throw error;
    }
  },

  async unlock(reason) {
    // 1. The OS prompts, and hands the item back only on a match.
    let session: BiometricSession;
    try {
      session = await readBiometricSession(reason);
    } catch (error) {
      // 2. The item is gone (biometrics changed since it was written): nothing is left to unlock.
      if ((error as { code?: string }).code === BIOMETRIC_SESSION_GONE) await get().signOut();
      return false;
    }

    // 3. Renew from the unlocked tokens before the app renders, as on any cold start.
    set({ ...session, locked: false, restoring: true });
    return true;
  },

  setLinkedTenants(tenants) {
    set({ linkedTenants: tenants });
    if (get().rememberSession) localStorage.setItem(SSO_TENANTS_KEY, JSON.stringify(tenants));
    else localStorage.removeItem(SSO_TENANTS_KEY);
  },

  /**
   * Sign out — mirrors Angular `AuthService.logout()`: clear credentials only.
   * Keeps `paas_tenant_id`, `paas_regional_api_url`, and branding (same hostname).
   */
  signOut() {
    // One sign-out at a time: its own calls (push unregister) can 401 and ask for another.
    signingOut ??= (async () => {
      // 1. Unregister the device's push token while the session can still authorise the call.
      await unregisterStoredToken();

      // 2. Revoke the refresh token server-side so a copy of it stops working. An unreachable
      //    server must not keep the member signed in on this device.
      const { refreshToken } = get();
      if (refreshToken) await revokeRefreshToken(refreshToken).catch(() => undefined);

      // 3. Clear the session, and its tokens wherever they were stored (the biometric-bound
      //    item included).
      const { tenantId, regionalApiUrl } = get();
      set({
        accessToken: null,
        refreshToken: null,
        user: null,
        tenantId,
        regionalApiUrl,
        ssoSessionToken: null,
        linkedTenants: [],
        restoring: false,
        locked: false,
      });
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(SSO_TENANTS_KEY);
      localStorage.removeItem(ACTIVE_PORTAL_DOMAIN_KEY);
      localStorage.removeItem(ACTIVE_COMMUNITY_TENANT_KEY);
      // Intentionally keep TENANT_ID_KEY + REGIONAL_API_URL_KEY for login/public APIs.
      await persistSecrets();

      // 4. Drop what belongs to the member who just left, so the next one on this device
      //    starts clean: their opt-ins, cart, home layout, and the documents they opened.
      useSettingsStore.getState().setBiometricEnabled(false);
      useSettingsStore.getState().setPushEnabled(false);
      await clearCart();
      useHomeLayoutStore.getState().reset();
      useQuickLinksStore.getState().reset();
      useNewsReadStore.getState().reset();
      await clearCachedDownloads();

      // 5. Drop the in-memory session data.
      usePropertyStore.getState().clear();
      usePendingJoinsStore.getState().clear();
      useModuleStore.getState().setConfig(null);
      useExperienceStore.getState().setExperience(null);
      syncLicenseModules(null);
      // Drop the previous member's cached queries (profile, properties, utilities,
      // etc.) now the session has ended — so re-logging in as a different user
      // can't surface stale data. Safe here: logged-out routing shows the login
      // screen, which has no member-data queries mounted.
      queryClient.clear();
    })().finally(() => { signingOut = null; });
    return signingOut;
  },

  /**
   * Signed-out residue sweep. `signOut()` is the clean path; a session that ended
   * without it — an expired refresh, a killed tab, keys left by an older build —
   * leaves the previous member behind in storage. A stale
   * `paas_active_community_tenant_id` is the sharp one: `domainForResolution()`
   * treats it as "the resident is inside a community" and keeps resolving that
   * community's domain instead of the configured pin, so the app boots against
   * the wrong tenant.
   *
   * Only member-session state is dropped. Deliberately kept:
   *   - `paas_tenant_id` / `paas_regional_api_url` — scope the public login and
   *     signup APIs; `signOut()` keeps them for the same reason.
   *   - `paas_active_portal_domain` — owned by `domainApi`, which already drops it
   *     when it doesn't match the env pin. Clearing it here would fight that.
   *   - `paas_resolved_*` root-tenant flags — resolution scope the login and
   *     welcome screens read to decide SSO vs per-domain; re-written every resolve.
   *   - branding / domain-config caches, theme, remembered identifier, and the
   *     signup OTP resend deadlines — device- or domain-level, not session state.
   *
   * No-op while a session is live, so a refresh-token-only session (which renders
   * the login screen, since `RedirectIfAuthed` only checks the access token) is safe,
   * and so is a session waiting behind the biometric lock.
   */
  clearStaleSession() {
    if (get().isAuthenticated() || get().locked) return;

    set({ accessToken: null, user: null, linkedTenants: [] });
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(SSO_TENANTS_KEY);
    localStorage.removeItem(ACTIVE_COMMUNITY_TENANT_KEY);

    // Both came from the member we just dropped: the licensed-module set, and any community
    // they were still waiting on.
    syncLicenseModules(null);
    usePendingJoinsStore.getState().clear();
  },

  getAccessToken() { return get().accessToken; },
  getRefreshToken() { return get().refreshToken; },
  getTenantId() { return get().tenantId; },
  getRegionalApiUrl() { return get().regionalApiUrl; },

  async hydrate() {
    // 1. The access token lives in memory only; delete the copy older builds left in storage.
    localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);

    const user = readJSON<User>(localStorage.getItem(USER_KEY));
    const tenantId = localStorage.getItem(TENANT_ID_KEY);
    const regionalApiUrl = localStorage.getItem(REGIONAL_API_URL_KEY) ?? '';
    const linkedTenants = readJSON<LinkedTenant[]>(localStorage.getItem(SSO_TENANTS_KEY)) ?? [];
    const rememberSession = localStorage.getItem(REMEMBER_SESSION_KEY) !== 'false';

    // 2. Nothing was kept: the session ended with the app.
    if (!rememberSession) {
      set({
        accessToken: null,
        refreshToken: null,
        user: null,
        tenantId,
        regionalApiUrl,
        ssoSessionToken: null,
        linkedTenants: [],
        rememberSession,
      });
      syncLicenseModules(null);
      return;
    }

    // 3. A session behind the biometric stays locked until the member unlocks it. Any other
    //    remembered session is renewed (`restoring`) before the app renders it.
    const locked = useSettingsStore.getState().biometricEnabled && (await hasBiometricSession());
    const refreshToken = locked ? null : await secureStorage.get(REFRESH_TOKEN_KEY);
    const ssoSessionToken = locked ? null : await secureStorage.get(SSO_SESSION_KEY);

    set({
      accessToken: null,
      refreshToken,
      user,
      tenantId,
      regionalApiUrl,
      ssoSessionToken,
      linkedTenants,
      rememberSession,
      locked,
      restoring: !!refreshToken,
    });
    syncLicenseModules(user);
  },
}));

export const AUTH_STORAGE_KEYS = {
  REFRESH_TOKEN: REFRESH_TOKEN_KEY,
  USER: USER_KEY,
  SSO_SESSION: SSO_SESSION_KEY,
  SSO_TENANTS: SSO_TENANTS_KEY,
  ACTIVE_PORTAL_DOMAIN: ACTIVE_PORTAL_DOMAIN_KEY,
  ACTIVE_COMMUNITY_TENANT: ACTIVE_COMMUNITY_TENANT_KEY,
  TENANT_ID: TENANT_ID_KEY,
  REGIONAL_API_URL: REGIONAL_API_URL_KEY,
  REMEMBER_SESSION: REMEMBER_SESSION_KEY,
};
